import {FormattedPhone} from '@rocket-ui/fusion';
import type {AxiosError} from 'axios';
import {useConfirmRecoveryCode} from '@rocket-ui/lk-api';
import {useUserInteraction} from '@rocket-ui/user-interaction';
import React, {FC} from 'react';
import {ExclamationMark} from 'shared/ui/ExclamationMark';
import {PasswordNumericKeypad} from './PasswordInputForm/PasswordNumericKeypad';
import {useAutoSubmitCodeLikeInput} from './useAutoSubmitCodeLikeInput';
import {useIsMaxSm} from './useIsMaxSm';
import {AuthorizationCodeTimer} from './CodeInputForm/AuthorizationCodeTimer';
import {CodeInput} from './CodeInputForm/CodeInput';
import styles from './CodeInputForm/CodeInputForm.module.css';

interface RecoveryPasswordTelegramCodeInputFormProps {
	phone: string;
	token: string;
	onSubmit: (token: string, clientStatus?: string) => void;
}

const SHAKE_DURATION_MS = 820;
const OTP_LENGTH = 4;

export const RecoveryPasswordTelegramCodeInputForm: FC<RecoveryPasswordTelegramCodeInputFormProps> = ({
	phone,
	token,
	onSubmit,
}) => {
	const {notifyUser} = useUserInteraction();
	const {mutateAsync: confirmRecoveryCode, isLoading} = useConfirmRecoveryCode();
	const isMaxSm = useIsMaxSm();

	const {
		value: code,
		onChange: handleChange,
		errorNonce,
		inputRef,
	} = useAutoSubmitCodeLikeInput({
		requiredLength: OTP_LENGTH,
		clearAfterErrorMs: SHAKE_DURATION_MS,
		notifyUser,
		errorMessageFallback: 'Ошибка при проверке кода',
		getErrorMessage: (err) => (err as AxiosError<{errors?: string[]}> | undefined)?.response?.data?.errors?.[0],
		submit: async (value) => {
			const data = await confirmRecoveryCode({token, otp: value});
			onSubmit(data.authToken, data.status as string);
		},
	});

	return (
		<section className={styles.section}>
			<p className={styles.title}>Введите код из Telegram</p>

			<div className={styles.inputWrap}>
				<CodeInput
					value={code}
					onChange={handleChange}
					disabled={isLoading}
					ref={inputRef as React.RefObject<HTMLInputElement>}
					errorNonce={errorNonce}
					suppressNativeKeyboard={isMaxSm}
				/>
			</div>

			<div className={styles.meta}>
				<p className={styles.hint}>
					Отправили его на номер <FormattedPhone value={phone} format={'+# (###) ###-##-##'} />
				</p>
				<div className={styles.footnote} role="note">
					<div className={styles.footnoteIcon}>
						<ExclamationMark className={styles.footnoteMark} />
					</div>
					<p className={styles.footnoteText}>
						Код приходит в системный чат «Коды подтверждения». Аккаунт Telegram должен быть привязан к этому номеру.
					</p>
				</div>
				<div className={styles.resendSlot}>
					<AuthorizationCodeTimer token={token} resendOtpStrategy="telegram_provider" recovery />
				</div>
			</div>

			<PasswordNumericKeypad
				value={code}
				maxLength={OTP_LENGTH}
				disabled={isLoading}
				onDigit={(digit) => handleChange((code + digit).slice(0, OTP_LENGTH))}
				onBackspace={() => handleChange(code.slice(0, -1))}
			/>
		</section>
	);
};
